import { useCallback, useEffect, useState } from 'react';
import { useTokens } from '../theme';
import {
  getSeats, listFundingRequests, setJobCreditPool, setOveragePool, submitFundingRequest,
} from '../services/institutions';
import {
  Card, SectionTitle, Stat, StatRow, Button, ErrorState, Field, dollars,
} from '../uiKit';
import { Toggle } from '../Toggle';
import { Switch } from '../Switch.jsx';
import { MetricsSkeleton } from '../Skeleton.jsx';
import { notifyError, notifySuccess } from '../toast.js';

/**
 * School-funded AI overage pool: enable it and set a $ cap. Sponsored students'
 * overage draws this pool; they are never charged personally. `institutionId`
 * scopes it for a super-admin.
 *
 * While the seats payload carries `jobCreditPool` (FEATURE_JOB_CREDITS on), a
 * "Job credits" toggle lets the school's own admins decide whether sponsored
 * students' extra job credits draw this pool (add-job-credits D14). It saves
 * directly, outside the request-only form, since it spends the approved pool
 * rather than changing it.
 */
export default function OveragePanel({ institutionId, requestOnly = false, canEdit = true }) {
  const t = useTokens();
  const [pool, setPool] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [capDollars, setCapDollars] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [jobPool, setJobPool] = useState(null);
  const [jobBusy, setJobBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [data, requestData] = await Promise.all([
        getSeats(institutionId),
        // A failed queue read (an admin without billing view gets a 403)
        // never fails the whole panel; the pool still renders.
        requestOnly
          ? Promise.resolve()
            .then(() => { return listFundingRequests(institutionId); })
            .catch(() => { return null; })
          : null,
      ]);
      const p = data?.overagePool || {};
      const pending = requestData?.requests?.find((request) => {
        return request.type === 'OVERAGE' && request.status === 'PENDING';
      }) || null;
      setPool(p);
      setJobPool(data?.jobCreditPool || null);
      setPendingRequest(pending);
      setEnabled(pending ? Boolean(pending.payload?.overageEnabled) : Boolean(p.enabled));
      const shownCap = pending?.payload?.overageCapCents ?? p.capCents;
      setCapDollars(shownCap ? String(shownCap / 100) : '');
    } catch (err) {
      setPool(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [institutionId, requestOnly]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const capCents = Math.max(0, Math.round((parseFloat(capDollars) || 0) * 100));
      if (requestOnly) {
        await submitFundingRequest({
          type: 'OVERAGE', payload: { overageEnabled: enabled, overageCapCents: capCents },
        }, institutionId);
      } else {
        await setOveragePool({ enabled, capCents }, institutionId);
      }
      await load();
      notifySuccess(requestOnly ? 'Overage funding request submitted.' : 'Overage pool updated.');
    } catch (err) {
      notifyError(err?.response?.data?.error
        || (requestOnly ? 'Could not submit the overage funding request.' : 'Could not update the overage pool.'));
    } finally {
      setBusy(false);
    }
  };

  const saveJobCredits = async (next) => {
    setJobBusy(true);
    try {
      // The answer carries the saved state, so the pool form (and any edit in
      // it) and the toggle's focus are left alone.
      const res = await setJobCreditPool(next, institutionId);
      setJobPool(res.jobCreditPool);
      notifySuccess(next ? 'Job credits can now draw the overage pool.' : 'Job credits no longer draw the overage pool.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not update job credits.');
    } finally {
      setJobBusy(false);
    }
  };

  if (loading) {
    return <Card><SectionTitle>Overage pool</SectionTitle><MetricsSkeleton count={3} /></Card>;
  }
  if (loadFailed) {
    return (
      <Card>
        <SectionTitle>Overage pool</SectionTitle>
        <ErrorState message="Couldn't load the overage pool." onRetry={load} />
      </Card>
    );
  }
  if (!pool) { return null; }
  return (
    <Card>
      <SectionTitle>Overage pool</SectionTitle>
      <StatRow>
        <Stat label="Status" value={pool.enabled ? 'On' : 'Off'} />
        <Stat label="Cap" value={dollars(pool.capCents)} />
        <Stat label="Spent" value={dollars(pool.spentCents)} />
      </StatRow>
      <p className="mb-4 mt-2 text-base" style={{ fontFamily: t.fonts.body, color: t.muted }}>
        When on, sponsored students&apos; AI overage is billed to the school (up to the cap). Students are never charged.
      </p>

      {pendingRequest ? (
        <p className="mb-4 text-sm" role="status" style={{ fontFamily: t.fonts.body, color: t.muted }}>
          Funding request pending. The current pool stays unchanged until a super admin approves it.
        </p>
      ) : null}

      {canEdit ? <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) { save(); }
        }}
      >
        <Switch checked={enabled} onChange={setEnabled} label="Overage enabled" />
        <div className="w-[140px]">
          <Field label="Monthly cap" value={capDollars} onChangeText={setCapDollars} inputMode="decimal" placeholder="Cap ($)" className="block mb-0" />
        </div>
        <Button
          label={busy ? 'Submitting…' : (requestOnly ? (pendingRequest ? 'Update request' : 'Request funding') : 'Save')}
          type="submit"
          disabled={busy}
        />
      </form> : null}

      {jobPool ? (
        <div role="group" aria-label="Job credits" className="mt-5 border-t pt-4" style={{ borderColor: t.hairline }}>
          <Toggle
            checked={Boolean(jobPool.enabled)}
            onChange={saveJobCredits}
            label="Job credits"
            disabled={!jobPool.canEdit || jobBusy}
          />
          <p className="mt-2 text-base" style={{ fontFamily: t.fonts.body, color: t.muted }}>
            Let students on school seats use extra job credits from this pool, at {dollars(jobPool.priceCents)} each. Off by default.
          </p>
          {pool.enabled ? null : (
            <p className="mt-1 text-base" style={{ fontFamily: t.fonts.body, color: t.muted }}>
              The overage pool must be on for this to take effect.
            </p>
          )}
          {jobPool.canEdit ? null : (
            <p className="mt-1 text-[13px]" style={{ fontFamily: t.fonts.mono, color: t.muted }}>
              Set by the school&apos;s admins
            </p>
          )}
        </div>
      ) : null}
    </Card>
  );
}
